import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { Spinner } from 'react-bootstrap';

const DeveloperFormLayer = () => {
    const [formData, setFormData] = useState({
        name: '',
        logo: '', // Base64 string
        contactNumber: '',
        email: '',
        address: '',
        // iban: '', // Bank details commented out
        // swiftCode: '', // Bank details commented out
        // nameOfAccount: '', // Bank details commented out
        // bankName: '', // Bank details commented out
        // branchNumber: '', // Bank details commented out
        // bankAddress: '', // Bank details commented out
        segelName: '',
        segelNumber: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState([]);
    const navigate = useNavigate();

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Handle file input and convert to Base64
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        const maxSize = 2 * 1024 * 1024; // 2MB

        if (file && file.size > maxSize) {
            setError(['File size exceeds 2MB.']);
            // Clear the file input to prevent submission
            e.target.value = null; // VERY IMPORTANT:  Reset the file input
        } else if (file) {
            setError([]); //Clear Previous Error if file valid
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData((prev) => ({ ...prev, logo: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError([]); // Clear previous errors

        // Create a payload without the bank details
        const payload = {
            name: formData.name,
            logo: formData.logo,
            contactNumber: formData.contactNumber,
            email: formData.email,
            address: formData.address,
            segelName: formData.segelName,
            segelNumber: formData.segelNumber,
            // Explicitly exclude bank details from being sent
        };


        try {
            // Send the modified payload
            const response = await api.post('/developers/register', payload);
            if (response.data.status) {
                toast.success('Developer submitted for approval.');
                navigate('/');
            }
        }
        catch (err) {
            if (err.response && err.response.data) {
                const data = err.response.data;

                if (data.response) {
                    // Prioritize errors within the 'response' object.
                    const errorDetails = Object.values(data.response).flat();
                    if (errorDetails.length > 0) {
                        setError(errorDetails);
                        setLoading(false); // Stop loading here
                        return; // Important: Exit early if we found 'response' errors.
                    }
                }

                if (data.message) {
                    setError([data.message]);
                } else {
                    setError(['An unexpected error occurred.']);
                }
            } else {
                setError(['An error occurred.  Please check your network connection.']);
            }
            setLoading(false); // Stop loading in the catch block
        }
        finally {
            setLoading(false); // Ensure loading stops even if there's an unexpected error
        }
    };

    return (
        <section className="auth bg-base d-flex flex-wrap">
            {/* Left Side (Image) */}
            <div className="auth-left d-lg-block d-none">
                <div className="d-flex align-items-center flex-column h-100 justify-content-center">
                    <img
                        src="assets/images/auth/3.jpg"
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                </div>
            </div>

            {/* Right Side (Form) */}
            <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">
                <div className="max-w-464-px mx-auto w-100">
                    <Link to="/" className="mb-40 max-w-290-px">
                        <h3>Brokerz</h3>
                    </Link>

                    <h6 className="mb-12">Register as a Developer</h6>
                    <p>
                        <span className="text-danger-600">
                            Fields with * are required.
                        </span>
                    </p>
                    {error.length > 0 && (
                        <ul className="text-danger mb-16">
                            {error.map((msg, index) => (
                                <li key={index}>{msg}</li>
                            ))}
                        </ul>
                    )}

                    <form onSubmit={handleSubmit}>

                        {/* Company Name (Required) */}
                        <div className="icon-field mb-16 required-field">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="f7:person" />
                            </span>
                            <input
                                type="text"
                                name="name"
                                placeholder="Developer Name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                                required
                            />
                        </div>

                        {/* Business Email (Required) */}
                        <div className="icon-field mb-16 required-field">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mage:email" />
                            </span>
                            <input
                                type="email"
                                name="email"
                                placeholder="Business Email"
                                value={formData.email}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                                required
                            />
                        </div>

                        {/* Contact Number (Required) */}
                        <div className="icon-field mb-16 required-field">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mage:phone" />
                            </span>
                            <input
                                type="tel"
                                name="contactNumber"
                                placeholder="Contact Number"
                                value={formData.contactNumber}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                                required
                            />
                        </div>

                        {/* Company Address (Optional) */}
                        <div className="icon-field mb-16">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mdi:address-marker" />
                            </span>
                            <input
                                type="text"
                                name="address"
                                placeholder="Developer Address"
                                value={formData.address}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                            />
                        </div>

                        {/* --- BANK DETAILS SECTION START (COMMENTED OUT) --- */}
                        {/*
                        // Account Name (Required)
                        <div className="icon-field mb-16 required-field">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mdi:account" />
                            </span>
                            <input
                                type="text"
                                name="nameOfAccount"
                                placeholder="Bank Account Holder Name"
                                value={formData.nameOfAccount}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                                required
                            />
                        </div>

                        // Bank Name (Required)
                        <div className="icon-field mb-16 required-field">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mdi:bank" />
                            </span>
                            <input
                                type="text"
                                name="bankName"
                                placeholder="Bank Name"
                                value={formData.bankName}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                                required
                            />
                        </div>

                        // IBAN (Required)
                        <div className="icon-field mb-16 required-field">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mdi:bank" />
                            </span>
                            <input
                                type="text"
                                name="iban"
                                placeholder="IBAN"
                                value={formData.iban}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                                required
                                pattern="^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$"
                                title="Enter a valid IBAN (e.g., GB29NWBK60161331926819)"
                            />
                        </div>


                        // Swift Code (Optional)
                        <div className="icon-field mb-16">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mdi:bank-transfer" />
                            </span>
                            <input
                                type="text"
                                name="swiftCode"
                                placeholder="Swift Code"
                                value={formData.swiftCode}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                            />
                        </div>

                        // Branch Number (Optional)
                        <div className="icon-field mb-16">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mdi:bank" />
                            </span>
                            <input
                                type="text"
                                name="branchNumber"
                                placeholder="Branch Number"
                                value={formData.branchNumber}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                            />
                        </div>

                        // Bank Address (Optional)
                        <div className="icon-field mb-16">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mdi:map-marker" />
                            </span>
                            <input
                                type="text"
                                name="bankAddress"
                                placeholder="Bank Address"
                                value={formData.bankAddress}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                            />
                        </div>
                         */}
                        {/* --- BANK DETAILS SECTION END (COMMENTED OUT) --- */}


                        {/* Segel Name (Required) */}
                        <div className="icon-field mb-16 required-field">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mdi:badge" />
                            </span>
                            <input
                                type="text"
                                name="segelName"
                                placeholder="Segel Name"
                                value={formData.segelName}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                                required
                            />
                        </div>

                        {/* Segel Number (Required) */}
                        <div className="icon-field mb-16 required-field">
                            <span className="icon top-50 translate-middle-y">
                                <Icon icon="mdi:id-card" />
                            </span>
                            <input
                                type="text"
                                name="segelNumber"
                                placeholder="Tax Number"
                                value={formData.segelNumber}
                                onChange={handleInputChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                                required
                            />
                        </div>

                        {/* Logo Upload (Required) */}
                        <div className="icon-field mb-16 required-field">
                            <input
                                type="file"
                                name="logo"
                                accept=".png, .jpg, .jpeg"
                                onChange={handleFileChange}
                                className="form-control h-56-px bg-neutral-50 radius-12"
                                required
                            />
                            <span className="mt-12 text-sm text-secondary-light">
                                Upload your developer logo (PNG, JPG) max 2MB.
                            </span>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32"
                            disabled={loading}
                        >
                            {loading ? <Spinner animation="border" size="sm" /> : 'Submit'}
                        </button>
                    </form>
                </div>
            </div>

            {/* Inline CSS for the red star pseudo-element */}
            <style>{`
        /* This ensures each required field .required-field has a red star inside the input */

        .required-field::after {
          content: "*";
          color: red;
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
        }
      `}</style>
        </section>
    );
};

export default DeveloperFormLayer;